import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaCreditCard, FaChartLine, FaUndo, FaSearch, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const AdminFinance = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    date_from: '',
    date_to: '',
    page: 1,
    limit: 20
  });

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [filters]);

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await adminAPI.getTransactions(filters);
      if (response.success) {
        setTransactions(response.transactions);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      console.log('📊 Fetching finance stats');
      const response = await adminAPI.getFinanceStats({ period: 30 });
      console.log('💰 Finance stats response:', response);

      if (response && response.success) {
        setStats(response);
        setError(null);
      } else {
        const errorMessage = response?.message || 'Unknown error occurred';
        console.error('❌ Finance stats API error:', errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error fetching finance stats:', error);

      // Safe error object access
      const errorObj = error || {};
      console.error('Error details:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data,
        code: errorObj.code,
        name: errorObj.name
      });

      let errorMessage = 'Failed to load statistics';

      // Check for network errors
      if (errorObj.code === 'NETWORK_ERROR' ||
          errorObj.code === 'ERR_NETWORK' ||
          errorObj.message?.includes('Network Error') ||
          errorObj.message?.includes('Failed to fetch') ||
          !errorObj.response) {
        errorMessage = '🔌 Backend server is not running. Please start the backend server on port 5117';
      } else if (errorObj.response?.status === 401) {
        errorMessage = '🔐 Unauthorized - please login again as admin';
      } else if (errorObj.response?.status === 403) {
        errorMessage = '🚫 Access denied - admin privileges required';
      } else if (errorObj.response?.status === 500) {
        errorMessage = '🔧 Server error - please try again later';
      } else if (errorObj.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj.message) {
        errorMessage = `Error: ${errorObj.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const handleRefundTransaction = async (transactionId, reason) => {
    try {
      const response = await adminAPI.refundTransaction(transactionId, {
        reason
      });
      if (response.data.success) {
        toast.success('Transaction refunded successfully');
        setShowRefundModal(false);
        fetchTransactions();
        fetchStats();
      }
    } catch (error) {
      console.error('Error refunding transaction:', error);
      toast.error('Failed to refund transaction');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Success</span>;
      case 0:
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Failed</span>;
      case 2:
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Refunded</span>;
      default:
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Unknown</span>;
    }
  };

  const getMethodBadge = (method) => {
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">{method}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const RefundModal = ({ transaction, onClose, onConfirm }) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!reason.trim()) {
        toast.error('Please provide a reason for the refund');
        return;
      }

      setLoading(true);
      await onConfirm(transaction.id, reason);
      setLoading(false);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Refund Transaction</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FaUndo className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm">
                <div><strong>Transaction:</strong> {transaction.transaction_name}</div>
                <div><strong>Amount:</strong> ${transaction.transaction_amount}</div>
                <div><strong>User:</strong> {transaction.user_name}</div>
                <div><strong>Date:</strong> {formatDate(transaction.created_at)}</div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Reason *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Please provide a reason for this refund..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Confirm Refund'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8 xl:space-y-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">Financial Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Monitor transactions and manage refunds</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <FaDollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue (30d)</p>
                <p className="text-2xl font-bold text-gray-900">${stats.revenue.total.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <FaChartLine className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Transaction</p>
                <p className="text-2xl font-bold text-gray-900">${stats.revenue.average.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <FaCreditCard className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">{stats.transactions.successRate}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <FaCalendarAlt className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.transactions.total}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue by Method */}
      {stats && stats.revenueByMethod.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Payment Method</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.revenueByMethod.map((method) => (
              <div key={method.method} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{method.method}</span>
                  <span className="text-lg font-bold text-green-600">${method.revenue.toFixed(2)}</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">{method.count} transactions</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Spenders */}
      {stats && stats.topSpenders.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Spenders</h2>
          <div className="space-y-3">
            {stats.topSpenders.slice(0, 5).map((spender, index) => (
              <div key={spender.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <span className="w-8 h-8 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-sm mr-3">
                    {index + 1}
                  </span>
                  <div>
                    <div className="font-medium">{spender.full_name}</div>
                    <div className="text-sm text-gray-500">{spender.email}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-green-600">${spender.totalSpent.toFixed(2)}</div>
                  <div className="text-sm text-gray-500">{spender.transactionCount} transactions</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="1">Successful</option>
              <option value="0">Failed</option>
              <option value="2">Refunded</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Types</option>
              <option value="vip">VIP</option>
              <option value="credits">Credits</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                fetchTransactions();
                fetchStats();
              }}
              className="w-full bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700"
            >
              <FaFilter className="inline w-4 h-4 mr-2" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{transaction.transaction_name}</div>
                    <div className="text-sm text-gray-500">ID: {transaction.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{transaction.user_name}</div>
                    <div className="text-sm text-gray-500">{transaction.user_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${transaction.transaction_amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getMethodBadge(transaction.method)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transaction.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {transaction.status === 1 && (
                      <button
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          setShowRefundModal(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Refund Transaction"
                      >
                        <FaUndo className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleFilterChange('page', pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft className="w-4 h-4" />
                </button>

                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handleFilterChange('page', pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Refund Modal */}
      {showRefundModal && selectedTransaction && (
        <RefundModal
          transaction={selectedTransaction}
          onClose={() => setShowRefundModal(false)}
          onConfirm={handleRefundTransaction}
        />
      )}
    </div>
  );
};

export default AdminFinance;